import React, { useEffect, useRef, useState } from 'react';
import { Loader2, TrendingUp, Zap, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';
import useIndustryProfilesList from '../../hooks/useIndustryProfilesList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LiveRecalcPanel — brings Solution Finder's "Configure & Model"
 * step onto Business Case Builder. The user can tweak audience
 * size, modality, time horizon, and industry right next to the
 * existing ROI read-out, and a debounced recalc updates the
 * `solution_data.financial_summary` in-place. A "Save changes"
 * button persists the new solution_data to the loaded case.
 *
 * Design contract:
 * - `solutionData` is the *current* solution_data (from parent state).
 * - `onDraftChange(newSolutionData)` fires on every successful recalc
 *   so the parent can update CaseInputsPanel + exports without a save.
 * - `onPersist(newSolutionData)` is called when the user clicks Save.
 * - Inputs disabled when the case is approved/submitted to prevent
 *   in-flight scope creep.
 */
export function LiveRecalcPanel({ solutionData, modalities = [], lockedReason, onDraftChange, onPersist }) {
  const { industries: industryProfiles } = useIndustryProfilesList();
  const kpi = solutionData?.kpi || {};
  const existingInterventions = solutionData?.interventions || [];
  const existingConfig = solutionData?.scenario_config || {};
  const locked = !!lockedReason;

  // Local editable copies
  const [edits, setEdits] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [lastRecalcAt, setLastRecalcAt] = useState(null);
  const [persisting, setPersisting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  // Seed edits from the latest solution_data — but ONLY when the
  // source case changes (KPI id flips). We must not clobber local
  // edits on every render, only when the underlying case changes.
  const seededForKpi = useRef(null);
  useEffect(() => {
    const key = `${kpi.id || ''}_${existingInterventions.length}`;
    if (seededForKpi.current === key) return;
    seededForKpi.current = key;
    setEdits({
      interventions: existingInterventions.map(iv => ({
        intervention_id: iv.intervention_id,
        intervention_name: iv.intervention_name,
        modality_id: iv.modality_id || 'blended',
        target_audience_size: iv.target_audience_size || 100,
        cost_override: iv.cost_override ?? null,
      })),
      time_horizon_months: existingConfig.time_horizon_months || 12,
      headcount: existingConfig.headcount || 500,
      industry_type: existingConfig.industry_type || 'technology',
    });
    setDirty(false);
  }, [kpi.id, existingInterventions, existingConfig]);

  // Debounced auto-recalc on edits
  useEffect(() => {
    if (!edits || !dirty || locked) return;
    const handle = setTimeout(() => {
      void runRecalc();
    }, 700);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, dirty, locked]);

  const patchIntervention = (idx, patch) => {
    setEdits(prev => {
      const next = { ...prev, interventions: [...prev.interventions] };
      next.interventions[idx] = { ...next.interventions[idx], ...patch };
      return next;
    });
    setDirty(true);
  };

  const patchConfig = (patch) => {
    setEdits(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const runRecalc = async () => {
    if (!edits || !kpi.id) return;
    setRecalculating(true);
    setError(null);
    const token = secureStorage.getItem('session_token');
    try {
      const res = await authedFetch(`${API_URL}/api/solution-finder/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          kpi_id: kpi.id,
          interventions: edits.interventions.map(iv => ({
            intervention_id: iv.intervention_id,
            modality_id: iv.modality_id,
            target_audience_size: iv.target_audience_size,
            cost_override: iv.cost_override,
          })),
          time_horizon_months: edits.time_horizon_months,
          headcount: edits.headcount,
          industry_type: edits.industry_type,
        }),
      });
      if (!res.ok) throw new Error('calc failed');
      const fresh = await res.json();
      setLastRecalcAt(Date.now());
      onDraftChange?.(fresh);
    } catch (e) {
      logger.error('live-recalc failed:', e);
      setError('Recalculation failed — last saved values are unchanged');
    } finally {
      setRecalculating(false);
    }
  };

  const handlePersist = async () => {
    setPersisting(true);
    try {
      await onPersist?.();
      setDirty(false);
      toast.success('Live recalc saved to case');
    } catch (e) {
      toast.error('Could not save');
    } finally {
      setPersisting(false);
    }
  };

  if (!solutionData || !kpi.id) return null;
  if (!edits) return null;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="live-recalc-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Zap size={14} className="text-amber-500" /> Live recalc
          <span className="text-[11px] font-normal text-slate-500 ml-1">
            tweak inputs — ROI re-computes automatically
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {recalculating && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <Loader2 className="animate-spin" size={12} /> recomputing…
            </span>
          )}
          {!recalculating && lastRecalcAt && (
            <span className="flex items-center gap-1 text-[11px] text-emerald-600">
              <CheckCircle size={12} /> recomputed
            </span>
          )}
          {dirty && !locked && (
            <button
              onClick={handlePersist}
              disabled={persisting || recalculating}
              className="text-xs font-semibold px-2.5 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
              data-testid="live-recalc-save-btn"
            >
              {persisting ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      {locked && (
        <div className="mb-3 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
          <AlertCircle size={12} />
          Inputs are locked: {lockedReason}
        </div>
      )}
      {error && (
        <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-800">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {edits.interventions.length === 0 && (
          <div className="text-xs text-slate-500 italic">
            No interventions selected — open this case in Solution Finder to add some.
          </div>
        )}
        {edits.interventions.map((iv, idx) => (
          <div
            key={iv.intervention_id}
            className="border border-slate-200 rounded-lg p-3 bg-slate-50 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3"
            data-testid={`live-recalc-row-${iv.intervention_id}`}
          >
            <div className="text-sm font-semibold text-slate-900">
              {iv.intervention_name || iv.intervention_id}
            </div>
            <label className="block text-[11px] text-slate-500">
              Modality
              <select
                value={iv.modality_id}
                disabled={locked}
                onChange={e => patchIntervention(idx, { modality_id: e.target.value })}
                className="w-full mt-1 px-2 py-1 text-sm rounded border border-slate-200 bg-white disabled:opacity-50"
                data-testid={`live-recalc-modality-${iv.intervention_id}`}
              >
                {modalities.length === 0 ? (
                  <option value={iv.modality_id}>{iv.modality_id}</option>
                ) : (
                  modalities.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))
                )}
              </select>
            </label>
            <label className="block text-[11px] text-slate-500">
              Audience
              <input
                type="number"
                min={1}
                disabled={locked}
                value={iv.target_audience_size}
                onChange={e =>
                  patchIntervention(idx, {
                    target_audience_size: Math.max(1, parseInt(e.target.value, 10) || 0),
                  })
                }
                className="w-24 mt-1 px-2 py-1 text-sm rounded border border-slate-200 bg-white disabled:opacity-50"
                data-testid={`live-recalc-size-${iv.intervention_id}`}
              />
            </label>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <label className="block text-[11px] text-slate-500">
          Time horizon
          <select
            value={edits.time_horizon_months}
            disabled={locked}
            onChange={e => patchConfig({ time_horizon_months: parseInt(e.target.value, 10) })}
            className="w-full mt-1 px-2 py-1 text-sm rounded border border-slate-200 bg-white disabled:opacity-50"
            data-testid="live-recalc-horizon"
          >
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
            <option value={36}>36 months</option>
          </select>
        </label>
        <label className="block text-[11px] text-slate-500">
          Headcount
          <input
            type="number"
            min={1}
            disabled={locked}
            value={edits.headcount}
            onChange={e => patchConfig({ headcount: Math.max(1, parseInt(e.target.value, 10) || 0) })}
            className="w-full mt-1 px-2 py-1 text-sm rounded border border-slate-200 bg-white disabled:opacity-50"
            data-testid="live-recalc-headcount"
          />
        </label>
        <label className="block text-[11px] text-slate-500">
          Industry
          <select
            value={edits.industry_type}
            disabled={locked}
            onChange={e => patchConfig({ industry_type: e.target.value })}
            className="w-full mt-1 px-2 py-1 text-sm rounded border border-slate-200 bg-white disabled:opacity-50"
            data-testid="live-recalc-industry"
          >
            {industryProfiles.length === 0 ? (
              <option value="technology">Loading…</option>
            ) : (
              industryProfiles.map((ind) => (
                <option key={ind.id} value={ind.id}>{ind.name}</option>
              ))
            )}
          </select>
        </label>
      </div>

      {solutionData.financial_summary && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-500">ROI</div>
            <div className="text-lg font-bold text-emerald-700 flex items-center justify-center gap-1">
              <TrendingUp size={14} />
              {solutionData.financial_summary.roi_percent != null
                ? `${(1 + (solutionData.financial_summary.roi_percent / 100)).toFixed(1)}x`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-500">Net benefit</div>
            <div className="text-lg font-bold text-slate-900">
              {solutionData.financial_summary.net_benefit != null
                ? `$${Number(solutionData.financial_summary.net_benefit).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-500">Payback</div>
            <div className="text-lg font-bold text-slate-900">
              {solutionData.financial_summary.payback_months != null
                ? `${Number(solutionData.financial_summary.payback_months).toFixed(1)} mo`
                : '—'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveRecalcPanel;
